/**
 * ROAM staging background upload queue.
 * Jobs survive closing the feature editor and in-app navigation, not a page refresh,
 * closing Safari, device sleep, or switching computers. Files remain in browser memory.
 * Sequential uploads avoid competing multi-GB transfers. Nothing is persisted remotely
 * until the Worker confirms the media object upload.
 */
import { uploadMedia } from './api';
import type { MediaUploadOptions } from '../types';

export type UploadState = 'queued' | 'uploading' | 'complete' | 'failed';
export interface UploadJobView {
  id: string;
  featureName: string;
  fileName: string;
  bytes: number;
  progress: number;
  state: UploadState;
  message: string;
}

type UploadJob = UploadJobView & {
  featureId: string;
  file: File;
  options: MediaUploadOptions;
};

export type NewUpload = { file: File; options: MediaUploadOptions };
type Listener = () => void;
const listeners = new Set<Listener>();
let jobs: UploadJob[] = [];
let view: readonly UploadJobView[] = [];
let processing = false;
let unloadWarningInstalled = false;

function onBeforeUnload(event: BeforeUnloadEvent) {
  if (!jobs.some((job) => job.state === 'queued' || job.state === 'uploading')) return;
  event.preventDefault();
  event.returnValue = '';
}

function publish() {
  // Public snapshot intentionally excludes File and upload options.
  view = jobs.map(({ id, featureName, fileName, bytes, progress, state, message }) =>
    ({ id, featureName, fileName, bytes, progress, state, message }));
  for (const listener of listeners) listener();
  const hasWork = jobs.some((job) => job.state === 'queued' || job.state === 'uploading');
  if (hasWork && !unloadWarningInstalled) {
    window.addEventListener('beforeunload', onBeforeUnload);
    unloadWarningInstalled = true;
  } else if (!hasWork && unloadWarningInstalled) {
    window.removeEventListener('beforeunload', onBeforeUnload);
    unloadWarningInstalled = false;
  }
}

export function subscribeToUploads(listener: Listener) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function getUploadSnapshot() { return view; }

export function enqueueFeatureUploads(featureId: string, featureName: string, files: NewUpload[]) {
  const ids: string[] = [];
  for (const { file, options } of files) {
    const id = crypto.randomUUID();
    ids.push(id);
    jobs.push({ id, featureId, featureName, fileName: file.name, bytes: file.size,
      file, options, progress: 0, state: 'queued', message: 'Waiting to upload' });
  }
  publish();
  void drainQueue();
  return ids;
}

async function drainQueue() {
  if (processing) return;
  processing = true;
  try {
    while (true) {
      const job = jobs.find((item) => item.state === 'queued');
      if (!job) break;
      job.state = 'uploading';
      job.message = 'Uploading securely';
      publish();
      try {
        await uploadMedia(job.featureId, job.file, job.options, (uploadedBytes) => {
          job.progress = Math.min(99, Math.round(100 * uploadedBytes / Math.max(job.bytes, 1)));
          publish();
        });
        job.progress = 100;
        job.state = 'complete';
        job.message = 'Ready for private review';
      } catch (error) {
        job.state = 'failed';
        job.message = error instanceof Error ? error.message : 'Upload failed. Retry this file.';
      }
      publish();
    }
  } finally {
    processing = false;
  }
}

export function retryUploadJob(id: string) {
  const job = jobs.find((item) => item.id === id && item.state === 'failed');
  if (!job) return;
  job.progress = 0;
  job.message = 'Waiting to retry';
  job.state = 'queued';
  publish();
  void drainQueue();
}

export function clearFinishedUploadJobs() {
  jobs = jobs.filter((job) => job.state === 'queued' || job.state === 'uploading');
  publish();
}
